import { useCallback, useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { toast } from '@/lib/toast';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { addRecentItem } from '@/hooks/useRecentItems';
import { LoadingState } from '@/components';
import type { IconName } from '@/components/ui/icon';
import { useBreadcrumbEffect, type BreadcrumbItem } from '@/components/blocks/chrome';
import {
  PageHeader,
  SectionHeader,
  TabNavigation,
  PageLayout,
  PageContent,
} from '@/components/blocks/page';
import { Section } from '@/components/blocks/layout';
import {
  ActivityBar,
  ActivityTile,
  Panel,
  ReportTable,
  TableFooterLink,
  type ActivityTileProps,
  type ReportTableRow,
} from '@/components/blocks/data-display';
import { EmptyState } from '@/components/blocks/feedback';
import type { ReportSummary } from '@/data/repos/reportsRepo';
import type { PersonRecord } from '@/data/schemas';
import { useDataProvider } from '@/data/provider';
import { queryKeys } from '@/lib/query-keys';
import { useCompany, useContextActivities, useCompleteAction, useStartActivity } from '../../hooks';
import { useSiteReports } from '../../../reports/hooks';
import { useEntityDevLogger } from '@/hooks/useEntityDevLogger';
import { useDialogState } from '@/hooks/useDialogState';
import { SiteModal } from '../CompanyDetail/SiteModal';
import { NewPersonnelModal } from '../CompanyDetail/NewPersonnelModal';
import { PersonnelModal } from '../CompanyDetail/PersonnelModal';
import { PersonnelCard } from '../../components/PersonnelCard';
import { RefrigerationUnitsOverview } from '../RefrigerationUnitDetail/RefrigerationUnitsOverview';
import { groupPersonnelByScope, sortPersonnelByPriority } from '@/data/personnel/personnelScope';
import { formatSiteShorthand, resolveSiteDisplayLabel } from '@/utils/entityIdentity';
import { formatIsoDateDDMMYYYY, formatRelativeDate, parseDateInput } from '@/utils/date';
import { getDateStatus } from '@/utils/dateStatus';
import { SiteAnagrafica } from '../../components/SiteAnagrafica';

function parseIsoDate(value: string | undefined | null): Date | null {
  if (!value) return null;
  return parseDateInput(value) ?? null;
}

function formatIsoDateLabel(value: string | undefined | null, fallback = '—'): string {
  if (!value) return fallback;
  const trimmed = value.trim();
  if (!trimmed) return fallback;
  return formatIsoDateDDMMYYYY(trimmed);
}

function sortReportsDesc(reports: ReportSummary[]): ReportSummary[] {
  return [...reports].sort((a, b) => {
    const aTime = parseIsoDate(a.updatedAt ?? a.visitDate ?? a.createdAt)?.getTime() ?? 0;
    const bTime = parseIsoDate(b.updatedAt ?? b.visitDate ?? b.createdAt)?.getTime() ?? 0;
    return bTime - aTime;
  });
}

export function SiteDetailScreen(): JSX.Element {
  const { sites } = useDataProvider();
  const navigate = useNavigate({
    from: '/companies/$companyId/sites/$siteId',
  });
  const params = useParams({ from: '/companies/$companyId/sites/$siteId' });
  const search = useSearch({ from: '/companies/$companyId/sites/$siteId' });

  const { data: company, isLoading } = useCompany(params.companyId);
  const { activities } = useContextActivities({
    companyId: params.companyId,
    siteId: params.siteId,
    maxItems: 5,
    urgentOnly: false,
  });
  const startActivityMutation = useStartActivity();
  const completeActivityMutation = useCompleteAction(params.companyId);
  const { data: reports = [], isLoading: loadingReports } = useSiteReports(params.siteId);

  const site = useMemo(() => {
    if (!company) return null;
    return company.sites.find((item) => item.id === params.siteId) ?? null;
  }, [company, params.siteId]);
  const siteDisplay = useMemo(() => {
    if (!company || !site) return null;
    return resolveSiteDisplayLabel(company, site);
  }, [company, site]);
  const siteDisplayName = siteDisplay?.name ?? site?.name ?? 'Stabilimento';

  const devPayload = useMemo(() => {
    if (!company || !site) return null;
    return {
      company,
      site,
      activities,
      reports,
    };
  }, [activities, company, reports, site]);

  useEntityDevLogger('Site detail', devPayload);

  // Track visit in recent items
  useEffect(() => {
    if (site) {
      addRecentItem({
        id: site.id,
        type: 'site',
        label: siteDisplayName,
      });
    }
  }, [site, siteDisplayName]);

  const queryClient = useQueryClient();
  const deleteSiteMutation = useMutation({
    mutationFn: async () => {
      if (!site) throw new Error('Stabilimento non disponibile');
      await sites.deleteSite(site.id);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.companies.all,
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.companies.detail(params.companyId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.sites.all,
        }),
      ]);
      toast.success('Stabilimento archiviato');
      navigate({
        to: '/companies/$companyId',
        params: { companyId: params.companyId },
        search: { tab: undefined },
      });
    },
  });

  const allowedTabs = ['operativita', 'anagrafica'] as const;
  type TabId = (typeof allowedTabs)[number];
  const isTabId = (value: unknown): value is TabId =>
    typeof value === 'string' && (allowedTabs as readonly string[]).includes(value as string);
  const rawTab = typeof search.tab === 'string' ? search.tab : undefined;
  const currentTab: TabId = isTabId(rawTab)
    ? rawTab
    : rawTab === 'registry'
      ? 'anagrafica'
      : rawTab === 'anagrafica'
        ? 'anagrafica'
        : 'operativita';

  const handleTabSelect = useCallback(
    (tabId: TabId) => {
      navigate({
        to: '/companies/$companyId/sites/$siteId',
        params: { companyId: params.companyId, siteId: params.siteId },
        search: {
          ...search,
          tab: tabId === 'operativita' ? undefined : tabId,
        },
        replace: true,
      });
    },
    [navigate, params.companyId, params.siteId, search]
  );

  const [isSiteDialogOpen, setSiteDialogOpen] = useDialogState('siteDetailEdit');
  const [selectedPersonnel, setSelectedPersonnel] = useState<PersonRecord | null>(null);
  const [personnelDialogOpen, setPersonnelDialogOpen] = useDialogState('siteDetailPersonnel');
  const [newPersonnelOpen, setNewPersonnelOpen] = useDialogState('siteDetailNewPersonnel');
  const siteShorthand = useMemo(() => {
    if (!company || !site) return null;
    return formatSiteShorthand(company, site);
  }, [company, site]);
  const siteContextLine = useMemo(() => {
    if (!company || !site) return undefined;
    if (siteShorthand) {
      return `${company.name} · ${siteDisplayName} · ${siteShorthand}`;
    }
    return `${company.name} · ${siteDisplayName}`;
  }, [company, site, siteDisplayName, siteShorthand]);
  const unitContextLine = useMemo(() => {
    if (!company || !site) return undefined;
    return `${company.name} · ${siteDisplayName}`;
  }, [company, site, siteDisplayName]);

  const breadcrumbFactory = useCallback(
    (baseTrail: BreadcrumbItem[]) => {
      const defaultTrail = [...baseTrail];

      if (!company || !site) {
        return [...defaultTrail, { label: 'Stabilimento' }];
      }

      const companyHref = `/companies/${company.id}`;

      return [
        ...defaultTrail,
        { label: company.name, href: companyHref },
        {
          label: siteDisplayName,
          href: `/companies/${company.id}/sites/${site.id}`,
        },
      ];
    },
    [company, site, siteDisplayName]
  );

  useBreadcrumbEffect(breadcrumbFactory);

  const sortedReports = useMemo(() => sortReportsDesc(reports), [reports]);
  const recentReports = useMemo(() => {
    const drafts = sortedReports.filter((report) => report.status === 'draft');
    const finalized = sortedReports.filter((report) => report.status === 'finalized');
    return [...drafts, ...finalized].slice(0, 5);
  }, [sortedReports]);
  const refrigerationUnits = useMemo(
    () => site?.refrigerationUnits ?? [],
    [site?.refrigerationUnits]
  );

  const personnelBreakdown = useMemo(() => {
    if (!company || !site) {
      return {
        site: [] as PersonRecord[],
      };
    }

    const grouped = groupPersonnelByScope(company.personnel ?? [], company.sites ?? []);
    const siteGroup = grouped.siteGroups.find((entry) => entry.siteId === site.id);

    return {
      site: sortPersonnelByPriority(siteGroup?.personnel ?? []),
    };
  }, [company, site]);

  const handleViewAllReports = useCallback(() => {
    if (!site) return;
    const reportQuery = [company?.name, site.name]
      .map((value) => value?.trim())
      .filter((value): value is string => Boolean(value && value.length > 0))
      .join(' ');
    navigate({
      to: '/reports',
      search: { query: reportQuery || undefined },
    });
  }, [company?.name, navigate, site]);

  const handleCreateReport = useCallback(() => {
    if (!company || !site) return;
    navigate({
      to: '/reports/$reportId',
      params: { reportId: 'new' },
      search: {
        companyId: company.id,
        siteId: site.id,
        mode: 'edit',
      },
    });
  }, [company, navigate, site]);

  const handleOpenReport = useCallback(
    (reportId: string, mode?: 'edit') => {
      navigate({
        to: '/reports/$reportId',
        params: { reportId },
        search: {
          companyId: undefined,
          siteId: undefined,
          mode,
        },
      });
    },
    [navigate]
  );

  const handleDeleteSite = useCallback(async () => {
    if (!site) return;
    await deleteSiteMutation.mutateAsync();
  }, [deleteSiteMutation, site]);

  const reportRows = useMemo<ReportTableRow[]>(
    () =>
      recentReports.map((report) => {
        const visitLabel = report.visitDate ? formatIsoDateLabel(report.visitDate, '—') : '—';
        const lastUpdatedLabel = report.updatedAt
          ? formatRelativeDate(report.updatedAt)
          : report.finalizedAt
            ? formatRelativeDate(report.finalizedAt)
            : '—';
        return {
          id: report.id,
          dateLabel: visitLabel,
          reportCode: report.title || '—',
          ncOpen: report.nonConformitiesOpen ?? 0,
          lastUpdatedLabel,
          status: report.status,
        };
      }),
    [recentReports]
  );

  const handleCreateUnit = useCallback(() => {
    if (!company || !site) return;
    navigate({
      to: '/companies/$companyId/sites/$siteId/units/$unitId',
      params: { companyId: company.id, siteId: site.id, unitId: 'new' },
      search: { tab: undefined, safety: undefined },
    });
  }, [company, navigate, site]);

  const hasUnits = refrigerationUnits.length > 0;

  const actionItems = useMemo(() => {
    type ActivityItem = { id: string } & Pick<
      ActivityTileProps,
      'title' | 'subtitle' | 'icon' | 'actionLabel' | 'onClick' | 'overdue'
    >;
    const items: ActivityItem[] = [];

    // If site has no units, show "Nuovo impianto" action first
    if (!hasUnits) {
      items.push({
        id: 'create-unit',
        title: `Stabilimento · ${siteDisplayName}`,
        subtitle: 'Aggiungi un impianto per iniziare',
        icon: 'ac_unit',
        actionLabel: 'Nuovo impianto',
        onClick: handleCreateUnit,
      });
    }

    // Add actual database activities
    activities.forEach((activity) => {
      const isOverdue = Boolean(activity.dueDate && getDateStatus(activity.dueDate) === 'expired');
      const dueLabel = activity.dueDate ? formatIsoDateLabel(activity.dueDate) : undefined;

      // Determine icon based on category
      let icon: IconName = 'task_alt';
      if (activity.category === 'inspection-reminder') {
        icon = 'event';
      } else if (activity.category === 'cpi-renewal') {
        icon = 'local_fire_department';
      } else if (activity.category === 'authorization') {
        icon = 'verified';
      } else if (activity.category === 'certification') {
        icon = 'badge';
      } else if (activity.category === 'manual') {
        icon = activity.reportId ? 'edit_document' : 'task_alt';
      }

      items.push({
        id: activity.id,
        title: activity.title,
        subtitle: dueLabel ? `Scad. ${dueLabel}` : undefined,
        icon,
        actionLabel: activity.status === 'pending' ? 'Avvia' : 'Completa',
        onClick: () => {
          if (activity.status === 'pending') {
            startActivityMutation.mutate(activity.id);
          } else {
            completeActivityMutation.mutate(activity.id);
          }
        },
        overdue: isOverdue,
      });
    });

    return items;
  }, [
    activities,
    completeActivityMutation,
    handleCreateUnit,
    hasUnits,
    siteDisplayName,
    startActivityMutation,
  ]);

  const handlePersonnelSelect = useCallback(
    (personnelId: string) => {
      const personnel = company?.personnel?.find((p) => p.id === personnelId);
      if (personnel) {
        setSelectedPersonnel(personnel);
        setPersonnelDialogOpen(true);
      }
    },
    [company?.personnel, setPersonnelDialogOpen]
  );

  if (isLoading || loadingReports) {
    return <LoadingState label="Caricamento stabilimento" />;
  }

  if (!company || !site) {
    return <></>;
  }

  const operativitaTabContent = (
    <Section spacing="lg">
      <Section>
        <SectionHeader title="Attività" />
        <ActivityBar emptyMessage="Nessuna attività prioritaria.">
          {actionItems.map((item) => (
            <ActivityTile
              key={item.id}
              title={item.title}
              subtitle={item.subtitle}
              icon={item.icon}
              actionLabel={item.actionLabel}
              onClick={item.onClick}
            />
          ))}
        </ActivityBar>
      </Section>

      <RefrigerationUnitsOverview
        companyId={company.id}
        siteId={site.id}
        contextLine={unitContextLine}
        refrigerationUnits={refrigerationUnits}
      />

      <Section>
        <SectionHeader
          title="Report recenti"
          action={
            hasUnits
              ? {
                  label: 'Nuovo report',
                  onClick: handleCreateReport,
                }
              : undefined
          }
        />
        {reportRows.length > 0 ? (
          <Panel
            contentClassName="p-0"
            className="overflow-hidden"
            footerClassName="p-0"
            footer={<TableFooterLink label="Tutti i report" onClick={handleViewAllReports} />}
          >
            <ReportTable
              rows={reportRows}
              showCompany={false}
              showSite={false}
              onRowClick={(row) =>
                row.status === 'draft' ? handleOpenReport(row.id, 'edit') : handleOpenReport(row.id)
              }
              emptyMessage="Nessun report recente"
              density="compact"
            />
          </Panel>
        ) : (
          <Panel>
            <EmptyState
              icon="assignment_turned_in"
              title="Nessun report recente"
              description="I report compariranno qui dopo la prima compilazione."
              variant="inline"
            />
          </Panel>
        )}
      </Section>
    </Section>
  );

  const anagraficaTabContent = (
    <Section spacing="lg">
      <Section>
        <SectionHeader
          title="Anagrafica stabilimento"
          action={{
            label: 'Modifica stabilimento',
            onClick: () => setSiteDialogOpen(true),
          }}
        />
        <SiteAnagrafica site={site} />
      </Section>

      <Section>
        <SectionHeader
          title="Personale"
          action={{
            label: 'Nuovo personale',
            onClick: () => setNewPersonnelOpen(true),
          }}
        />
        {personnelBreakdown.site.length > 0 ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {personnelBreakdown.site.map((person) => (
              <PersonnelCard key={person.id} person={person} onEdit={handlePersonnelSelect} />
            ))}
          </div>
        ) : (
          <Panel>
            <EmptyState
              icon="group_off"
              title="Nessun contatto assegnato"
              description={`Aggiungi il primo contatto per ${siteDisplayName}.`}
              variant="inline"
            />
          </Panel>
        )}
      </Section>
    </Section>
  );

  const tabContent = currentTab === 'anagrafica' ? anagraficaTabContent : operativitaTabContent;

  return (
    <PageLayout>
      <PageHeader title={siteDisplayName} icon="factory" />

      <TabNavigation
        activeTab={currentTab}
        onSelect={(tabId) => handleTabSelect(tabId as TabId)}
        tabs={[
          { id: 'operativita', label: 'Operatività' },
          { id: 'anagrafica', label: 'Anagrafica' },
        ]}
      />

      <PageContent>{tabContent}</PageContent>

      <SiteModal
        site={site}
        open={isSiteDialogOpen}
        onOpenChange={setSiteDialogOpen}
        contextLine={siteContextLine}
        onDelete={handleDeleteSite}
        isDeleting={deleteSiteMutation.isPending}
      />

      <PersonnelModal
        personnel={selectedPersonnel}
        companyId={company.id}
        companyCode={company.code}
        sites={company.sites}
        open={personnelDialogOpen}
        onOpenChange={setPersonnelDialogOpen}
      />

      <NewPersonnelModal
        companyId={company.id}
        companyCode={company.code}
        sites={company.sites}
        defaultScope="site"
        defaultSiteId={site.id}
        lockScope
        open={newPersonnelOpen}
        onOpenChange={setNewPersonnelOpen}
      />
    </PageLayout>
  );
}
